import React from "react";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import "./kuika-component-icon.scss";

export interface KuikaComponentIconProps {
  /** Component or icon name; resolved through the asset map. Ignored when `src` is given. */
  name?: string;
  /** A url the caller resolved itself (e.g. from a component definition). */
  src?: string;
  width?: number;
  height?: number;
  style?: React.CSSProperties;
  onClick?: (e?: React.MouseEvent<HTMLDivElement>) => void;
  className?: string;
  id?: string;
}

/**
 * The revision of the glyph files this component paints. The files kept their paths when they
 * were redrawn as masks, and /images is served without Cache-Control, so a browser that had held
 * the old artwork kept reusing it from its heuristic cache — an opaque tile the mask turned into a
 * solid grey square. A new revision is a new url, which no cache holds. Bump it whenever a glyph
 * under /images/componentIcons changes in place.
 */
export const COMPONENT_ICON_REVISION: string = "2";

export const withComponentIconRevision = (url: string): string => {
  const separator: string = url.includes("?") ? "&" : "?";
  return `${url}${separator}v=${COMPONENT_ICON_REVISION}`;
};

/**
 * A component's glyph, painted as a mask so the stylesheet owns its colour. Hover, selection and
 * the theme are all just --kuika-svg-color on an ancestor; this component has no state of its own.
 */
const KuikaComponentIcon: React.FC<KuikaComponentIconProps> = ({
  name,
  src,
  width = 40,
  height = 40,
  style,
  onClick,
  className,
  id
}) => {
  const maskUrl: string = withComponentIconRevision(src ?? KMainFunctions.getImageUrlByIconName(name));

  return (
    <div
      id={id}
      className={`kuika-component-icon kuika-component__icon ${id ?? ""} ${className ?? ""}`}
      style={
        {
          ...style,
          "--kuika-svg-width": `${width}px`,
          "--kuika-svg-height": `${height}px`,
          "--kuika-svg-mask": `url(${maskUrl})`
        } as React.CSSProperties
      }
      onClick={onClick}
      data-onboarding-id={`component-icon_${name?.replace(/\s/g, "").toLowerCase() ?? ""}`}
    />
  );
};

export default KuikaComponentIcon;
